import React, { useEffect, useState } from 'react';
import { TickerTapeProvider, useTickerTape } from './TickerTapeContext';
import { StockDirection } from './types';
import { GameInstructions } from './GameInstructions';
import { useAuth } from '../../context/FirebaseAuthContext';
import { useGameSession } from '../../context/GameSessionContext';
import { GameResultSaver } from '../../utils/gameResultSaver';
import './TickerTape.css';

const TickerDisplay: React.FC = () => {
  const { state } = useTickerTape();

  const animationDuration = `${state.difficulty.displayTime}s`;

  return (
    <div className="ticker-container">
      <div 
        className="ticker-tape" 
        style={{ 
          animationDuration: animationDuration 
        }}
      >
        {state.stocks.map((stock) => (
          <div key={stock.id} className="ticker-item">
            <span className="stock-symbol">{stock.symbol}</span>
            <span className={`direction-arrow ${stock.direction.toLowerCase()}`}>
              {stock.direction === 'UP' ? '⬆️' : '⬇️'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const RecallInterface: React.FC = () => {
  const { state, submitAnswer, completeRound } = useTickerTape();

  const handleDirectionSelect = (stockSymbol: string, direction: StockDirection) => {
    submitAnswer(stockSymbol, direction);
  };

  const allAnswered = state.stocks.every(stock => 
    state.playerAnswers.hasOwnProperty(stock.symbol)
  );

  return (
    <div className="recall-interface">
      <h3 className="text-xl font-semibold mb-4 text-center">
        Which direction did each stock move?
      </h3>
      <div className="stocks-grid">
        {state.stocks.map((stock) => (
          <div key={stock.symbol} className="stock-question">
            <div className="stock-symbol-large">{stock.symbol}</div>
            <div className="direction-buttons">
              <button
                className={`direction-btn up ${
                  state.playerAnswers[stock.symbol] === 'UP' ? 'selected' : ''
                }`}
                onClick={() => handleDirectionSelect(stock.symbol, 'UP')}
              >
                ⬆️ UP
              </button>
              <button
                className={`direction-btn down ${
                  state.playerAnswers[stock.symbol] === 'DOWN' ? 'selected' : ''
                }`}
                onClick={() => handleDirectionSelect(stock.symbol, 'DOWN')}
              >
                ⬇️ DOWN
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {allAnswered && (
        <div className="text-center mt-6">
          <button 
            className="next-round-btn"
            onClick={completeRound}
          >
            Complete Round
          </button>
        </div>
      )}
    </div>
  );
};

const ResultScreen: React.FC = () => {
  const { state, nextRound, endGame, resetGame } = useTickerTape();

  // Calculate results for current round
  const correctAnswers = state.stocks.filter(stock => 
    state.playerAnswers[stock.symbol] === stock.direction
  ).length;

  const currentAccuracy = correctAnswers / state.stocks.length;

  return (
    <div className="result-screen">
      <h2 className="text-2xl font-bold text-center mb-6">Round {state.currentRound} Results</h2>
      
      <div className="results-summary">
        <div className="accuracy-display">
          <span className="accuracy-label">Accuracy:</span>
          <span className="accuracy-value">
            {Math.round(currentAccuracy * 100)}%
          </span>
        </div>
        <div className="score-display">
          <span className="score-label">Correct:</span>
          <span className="score-value">
            {correctAnswers} / {state.stocks.length}
          </span>
        </div>
      </div>

      <div className="difficulty-info">
        <h3 className="font-semibold mb-2">Current Difficulty:</h3>
        <p>Stocks: {state.difficulty.stockCount}</p>
        <p>Speed: {Math.round(state.difficulty.scrollSpeed)} px/s</p>
      </div>

      <div className="round-details">
        <h3 className="font-semibold mb-2">Your Answers:</h3>
        <div className="answers-grid">
          {state.stocks.map((stock) => {
            const isCorrect = state.playerAnswers[stock.symbol] === stock.direction;
            return (
              <div key={stock.symbol} className={`answer-item ${isCorrect ? 'correct' : 'incorrect'}`}>
                <span className="stock-name">{stock.symbol}</span>
                <span className="actual-direction">
                  Actual: {stock.direction === 'UP' ? '⬆️' : '⬇️'}
                </span>
                <span className="player-answer">
                  Your Answer: {state.playerAnswers[stock.symbol] 
                    ? (state.playerAnswers[stock.symbol] === 'UP' ? '⬆️' : '⬇️')
                    : '❓'}
                </span>
                <span className="result-icon">
                  {isCorrect ? '✅' : '❌'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="action-buttons">
        {state.currentRound < 10 && !state.isComplete ? (
          <button className="next-round-btn" onClick={nextRound}>
            Next Round
          </button>
        ) : (
          <div className="game-complete">
            <h3 className="text-xl font-bold mb-4">Game Complete!</h3>
            <p className="mb-4">
              Overall Accuracy: {Math.round(state.totalAccuracy * 100)}%
            </p>
            <button className="play-again-btn" onClick={resetGame}>
              Play Again
            </button>
          </div>
        )}
        <button className="end-game-btn" onClick={endGame}>
          End Game
        </button>
      </div>
    </div>
  );
};

const GameTimer: React.FC = () => {
  const { state, dispatch } = useTickerTape();

  useEffect(() => {
    if (state.timeLeft > 0 && (state.phase === 'watching' || state.phase === 'recalling')) {
      const timer = setInterval(() => {
        dispatch({ type: 'UPDATE_TIME', payload: state.timeLeft - 1 });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [state.timeLeft, state.phase, dispatch]);

  if (state.phase === 'result' || state.isComplete || state.phase === 'instructions') return null;

  return (
    <div className="game-timer">
      <div className="timer-display">
        <span className="timer-label">
          {state.phase === 'watching' ? 'Watch the tickers:' : 'Time remaining:'}
        </span>
        <span className="timer-value">{state.timeLeft}s</span>
      </div>
      <div className="progress-bar">
        <div 
          className="progress-fill"
          style={{ 
            width: `${(state.timeLeft / (state.phase === 'watching' ? state.difficulty.displayTime : 30)) * 100}%` 
          }}
        />
      </div>
    </div>
  );
};

const TickerTapeGameContent: React.FC = () => {
  const { state, startGame } = useTickerTape();
  const { user } = useAuth();
  const { currentSession } = useGameSession();
  const [lastSavedRound, setLastSavedRound] = useState(0);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second for live timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getLevelTime = () => {
    if (!state.levelStartTime) return 0;
    return Math.floor((currentTime - state.levelStartTime) / 1000);
  };

  // Save results to database when round is completed
  useEffect(() => {
    if (state.rounds.length > 0 && state.phase === 'result' && state.rounds.length > lastSavedRound) {
      console.log('TickerTape: Triggering database save for round', state.currentRound, 'with', state.rounds.length, 'total rounds');
      const saveRoundToDatabase = async () => {
        const lastRound = state.rounds[state.rounds.length - 1];
        const correctAnswers = Object.keys(lastRound.playerAnswers).filter(symbol => 
          lastRound.playerAnswers[symbol] === lastRound.stocks.find(s => s.symbol === symbol)?.direction
        ).length;
        
        const reactionTime = (lastRound.endTime || Date.now()) - lastRound.startTime;
        const reactionTimeSeconds = reactionTime / 1000;
        const score = Math.round(lastRound.accuracy * 1000 + Math.max(0, 10 - reactionTimeSeconds) * 10);

        const resultData = {
          sessionId: currentSession?.sessionId || `session-${Date.now()}`,
          timestamp: new Date().toISOString(),
          round: state.currentRound,
          stocksCount: state.stocks.length,
          correctAnswers: correctAnswers,
          totalAnswers: state.stocks.length,
          accuracyPercentage: lastRound.accuracy * 100,
          scrollSpeedPixelsPerSecond: Math.round(100 / state.difficulty.displayTime), // Approximation
          displayTimeSeconds: state.difficulty.displayTime,
          reactionTimeSeconds: reactionTimeSeconds,
          difficultyLevel: state.difficulty.stockCount,
          isGameCompleted: state.isComplete,
          overallAccuracy: state.totalAccuracy * 100,
          stockData: JSON.stringify(lastRound.stocks),
          playerAnswers: JSON.stringify(lastRound.playerAnswers),
          ddaData: JSON.stringify({
            difficulty: state.difficulty,
            roundAccuracy: lastRound.accuracy,
            totalRounds: state.rounds.length
          })
        };

        console.log('TickerTape: Saving result data:', resultData);
        await GameResultSaver.saveResultSafely(
          () => GameResultSaver.saveTickerTapeResult(resultData),
          'TickerTape',
          resultData
        );

        // Mark this round as saved
        setLastSavedRound(state.rounds.length);
      };

      saveRoundToDatabase();
    }
  }, [state.rounds.length, state.phase, state.currentRound, state.stocks, state.difficulty, 
      state.isComplete, state.totalAccuracy, state.rounds, currentSession, lastSavedRound]);

  const getPhaseDescription = () => {
    switch (state.phase) {
      case 'instructions':
        return 'Read the instructions carefully before starting the game.';
      case 'watching':
        return 'Watch the stock tickers carefully! Remember which direction each stock moved.';
      case 'recalling':
        return 'Now select the direction each stock moved during the ticker display.';
      case 'result':
        return 'Round complete! Check your accuracy and get ready for the next round.';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-red-50 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-3 sm:px-4">
        <header className="text-center mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2">Ticker Tape Rush</h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-4 px-2">
            {getPhaseDescription()}
          </p>
          {state.phase !== 'instructions' && (
            <div className="game-info flex flex-col sm:flex-row sm:justify-center sm:space-x-6 space-y-2 sm:space-y-0 text-sm sm:text-base">
              <span className="round-info">Round {state.currentRound} of 10</span>
              <span className="timer-info">Level Time: {formatTime(getLevelTime())}</span>
              <span className="difficulty-info">
                Difficulty: {state.difficulty.stockCount} stocks, {Math.round(state.difficulty.scrollSpeed)} px/s
              </span>
            </div>
          )}
        </header>

        <GameTimer />

        <div className="game-content">
          {state.phase === 'instructions' && <GameInstructions onStartGame={startGame} />}
          {state.phase === 'watching' && <TickerDisplay />}
          {state.phase === 'recalling' && <RecallInterface />}
          {state.phase === 'result' && <ResultScreen />}
        </div>

        {state.isComplete && (
          <div className="final-results">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">Final Results</h2>
            <div className="final-stats text-center space-y-2">
              <p>Total Rounds: {state.rounds.length}</p>
              <p>Average Accuracy: {Math.round(state.totalAccuracy * 100)}%</p>
              <p>Final Difficulty: {state.difficulty.stockCount} stocks</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const TickerTapeGame: React.FC = () => {
  return (
    <TickerTapeProvider>
      <TickerTapeGameContent />
    </TickerTapeProvider>
  );
};